import { useRef, useState, useEffect } from 'react';
import { ArrowLeft, Camera, Moon, Sun, Bell, Clock, Award, User, Settings, Baby, Smile, LogOut, Cloud, Save, CheckCircle2, Volume2 } from 'lucide-react';
import { useProfile } from '../hooks/useProfile';
import { useFootprint } from '../hooks/useFootprint';
import { useAuth } from '../hooks/useAuth';
import { useNotifications } from '../hooks/useNotifications';
import { AuthModal } from './AuthModal';
import { Footprint } from './Footprint';
import { MiniMasjid } from './MiniMasjid';

interface ProfilePageProps {
  onBack: () => void;
}

export function ProfilePage({ onBack }: ProfilePageProps) {
  const { profile, updateProfile, updateSettings } = useProfile();
  const { deeds, setHablCoins } = useFootprint(); // Extract setHablCoins
  const { session, signOut } = useAuth();
  const { sendNotification, requestPermission } = useNotifications();
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto-update Display Name if Full Name is set and Display Name is still default
  useEffect(() => {
    if (profile.details?.fullName && (profile.name === 'Username' || profile.name === 'Musafir')) {
      const firstName = profile.details.fullName.split(' ')[0];
      if (firstName) {
        updateProfile({ name: firstName });
      }
    }
  }, [profile.details?.fullName]);

  const handleSave = () => {
    setIsSaving(true);
    // Simulate network delay for UX
    setTimeout(() => {
      setIsSaving(false);
      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 2000);
    }, 800);
  };

  // Calculate stats for badges
  const totalDeeds = Object.values(deeds).reduce((a, b) => a + b, 0);

  // Profile Completion Logic
  const filledFields = [
    profile.details?.fullName,
    profile.details?.phone,
    profile.details?.location,
    profile.avatar
  ].filter(Boolean).length;
  const totalFields = 4; // FullName, Phone, Location, Avatar
  const completionPercentage = Math.round((filledFields / totalFields) * 100);

  // Badge Logic
  const hasFullProfile = profile.details?.fullName && profile.details?.phone && profile.details?.location;
  
  const badges = [
    { id: 'seeker', name: 'The Seeker', icon: '🌱', threshold: 1, desc: 'Started the journey' },
    { id: 'consistent', name: 'Consistent', icon: '⭐', threshold: 10, desc: '10 Good Deeds' },
    { id: 'devoted', name: 'Devoted', icon: '🕌', threshold: 50, desc: '50 Good Deeds' },
    { id: 'guardian', name: 'Guardian', icon: '🛡️', threshold: 100, desc: '100 Good Deeds' },
    { id: 'citizen', name: 'Habl Citizen', icon: '🆔', threshold: 0, unlocked: !!hasFullProfile, desc: 'Complete Profile' },
  ];

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        updateProfile({ avatar: reader.result as string });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleNotificationToggle = async () => {
    if (!profile.settings.notifications) {
      // User is trying to turn ON notifications
      const granted = await requestPermission();
      if (granted) {
          updateSettings({ notifications: true });
          sendNotification('Habl Notifications Enabled', { 
            body: 'You will now receive prayer alerts.' 
          });
      } else {
          alert('Please enable notifications in your device settings to receive prayer alerts.');
      }
    } else {
      // Turning OFF
      updateSettings({ notifications: false });
    }
  };

  const handleTestNotification = () => {
    sendNotification('Test Alert 🔔', { 
        body: 'This is how prayer notifications will appear on your device.' 
    });
  };

  return (
    <div className={`min-h-screen ${profile.settings.darkMode ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-900'} transition-colors duration-300 pb-8`}>
      {/* Header */}
      <div className={`p-6 pt-safe-top flex items-center gap-4 sticky top-0 z-10 ${profile.settings.darkMode ? 'bg-slate-900/95 border-slate-800' : 'bg-white/95 border-slate-200'} border-b backdrop-blur-md`}>
        <button 
          onClick={onBack}
          className={`p-2 -ml-2 rounded-full ${profile.settings.darkMode ? 'hover:bg-slate-800 text-slate-400' : 'hover:bg-slate-100 text-slate-600'} transition-colors`}
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h2 className="text-xl font-bold">Profile & Settings</h2>
      </div>

      <div className="p-6 max-w-md mx-auto space-y-8">
        
        {/* Profile Card */}
        <div className={`rounded-3xl p-6 text-center relative overflow-visible ${profile.settings.darkMode ? 'bg-slate-800 border border-slate-700' : 'bg-white shadow-sm border border-slate-100'}`}>
          
          {/* Floating Badges */}
          {badges.filter(b => b.id !== 'citizen').slice(0, 4).map((badge, index) => {
            const isUnlocked = badge.unlocked !== undefined ? badge.unlocked : totalDeeds >= badge.threshold;
            // Calculate position based on index (up to 4)
            // 0: Top Left, 1: Top Right, 2: Bottom Right, 3: Bottom Left
            const positions = [
              'top-4 left-4',
              'top-4 right-4',
              'bottom-4 right-4',
              'bottom-4 left-4'
            ];
            const positionClass = positions[index];
            const delay = index * 500; // Stagger bounces

            return (
              <div 
                key={badge.id}
                className={`absolute ${positionClass} z-20 w-10 h-10 rounded-full flex items-center justify-center text-xl shadow-sm border border-white/20 animate-bounce 
                  ${profile.settings.darkMode ? 'bg-slate-700' : 'bg-white'}
                  ${isUnlocked ? 'opacity-100' : 'opacity-40 grayscale'}
                `}
                style={{ animationDuration: '3s', animationDelay: `${delay}ms` }}
                title={isUnlocked ? badge.name : `Unlock at ${badge.threshold} deeds`}
              >
                {badge.icon}
              </div>
            );
          })}

          <div className="relative inline-block mb-4 group">
            <div className={`w-24 h-24 rounded-full overflow-hidden border-4 ${profile.settings.darkMode ? 'border-slate-700' : 'border-slate-100'} mx-auto relative z-10`}>
              {profile.avatar ? (
                <img src={profile.avatar} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <div className={`w-full h-full flex items-center justify-center ${profile.settings.darkMode ? 'bg-slate-700 text-slate-500' : 'bg-slate-100 text-slate-400'}`}>
                  <User className="w-10 h-10" />
                </div>
              )}
            </div>
            <button 
              onClick={() => fileInputRef.current?.click()}
              className="absolute bottom-0 right-0 p-2 bg-emerald-500 rounded-full text-white shadow-lg hover:bg-emerald-600 transition-colors z-30"
            >
              <Camera className="w-4 h-4" />
            </button>
            <input 
              type="file" 
              ref={fileInputRef} 
              className="hidden" 
              accept="image/*"
              onChange={handleImageUpload} 
            />
          </div>

          <div className="space-y-4">
            <input
              type="text"
              value={profile.name}
              onChange={(e) => updateProfile({ name: e.target.value })}
              className={`text-2xl font-bold text-center w-full bg-transparent focus:outline-none focus:ring-2 focus:ring-emerald-500/50 rounded-lg px-2 ${profile.settings.darkMode ? 'text-white' : 'text-slate-900'}`}
              placeholder="Username"
            />
            
            <div className={`p-4 rounded-xl space-y-4 ${profile.settings.darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
              <div className="flex items-center justify-between">
                <h4 className={`text-xs font-bold uppercase tracking-wider ${profile.settings.darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Private Details</h4>
                <span className={`text-xs font-bold ${completionPercentage === 100 ? 'text-emerald-500' : 'text-slate-400'}`}>
                  {completionPercentage}% Complete
                </span>
              </div>
              
              {/* Progress Bar */}
              <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all duration-1000 ease-out ${completionPercentage === 100 ? 'bg-emerald-500' : 'bg-emerald-400'}`}
                  style={{ width: `${completionPercentage}%` }}
                />
              </div>

              <div className="space-y-3 pt-2">
                <input
                  type="text"
                  value={profile.details?.fullName || ''}
                  onChange={(e) => updateProfile({ details: { ...profile.details, fullName: e.target.value } })}
                  className={`w-full bg-transparent border-b ${profile.settings.darkMode ? 'border-slate-600 text-white placeholder-slate-500' : 'border-slate-200 text-slate-900 placeholder-slate-400'} py-2 text-sm focus:outline-none focus:border-emerald-500 transition-colors`}
                  placeholder="Full Name"
                />
                
                <input
                  type="tel"
                  value={profile.details?.phone || ''}
                  onChange={(e) => updateProfile({ details: { ...profile.details, phone: e.target.value } })}
                  className={`w-full bg-transparent border-b ${profile.settings.darkMode ? 'border-slate-600 text-white placeholder-slate-500' : 'border-slate-200 text-slate-900 placeholder-slate-400'} py-2 text-sm focus:outline-none focus:border-emerald-500 transition-colors`}
                  placeholder="Phone Number"
                />
                
                <input
                  type="text"
                  value={profile.details?.location || ''}
                  onChange={(e) => updateProfile({ details: { ...profile.details, location: e.target.value } })}
                  className={`w-full bg-transparent border-b ${profile.settings.darkMode ? 'border-slate-600 text-white placeholder-slate-500' : 'border-slate-200 text-slate-900 placeholder-slate-400'} py-2 text-sm focus:outline-none focus:border-emerald-500 transition-colors`}
                  placeholder="Location (City, Country)"
                />
              </div>

              <button 
                onClick={handleSave}
                disabled={isSaving || showSaved}
                className={`w-full py-2.5 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-all ${
                  showSaved 
                    ? 'bg-emerald-500 text-white' 
                    : 'bg-slate-800 text-white hover:bg-slate-900 active:scale-95'
                }`}
              >
                {showSaved ? (
                  <>
                    <CheckCircle2 className="w-4 h-4" /> Saved!
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" /> {isSaving ? 'Saving...' : 'Save Changes'}
                  </>
                )}
              </button>
            </div>

            <input
              type="text"
              value={profile.bio}
              onChange={(e) => updateProfile({ bio: e.target.value })}
              className={`text-sm text-center w-full bg-transparent focus:outline-none focus:ring-2 focus:ring-emerald-500/50 rounded-lg px-2 ${profile.settings.darkMode ? 'text-slate-400' : 'text-slate-500'}`}
              placeholder="Add a bio..."
            />
          </div>

          {/* Auth / Sync Status */}
          <div className="mt-6 flex justify-center">
            {session ? (
              <div className="flex items-center gap-2 px-4 py-2 bg-emerald-500/10 text-emerald-600 rounded-full text-xs font-bold border border-emerald-500/20">
                <Cloud className="w-3 h-3" />
                <span>Synced as {session.email?.split('@')[0]}</span>
                <button 
                  onClick={() => {
                    signOut();
                    window.location.reload(); // Force reload to clear state/hooks and show WelcomeScreen
                  }} 
                  className="ml-2 p-1 hover:bg-emerald-500/20 rounded-full" 
                  title="Sign Out"
                >
                  <LogOut className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <button 
                onClick={() => setIsAuthOpen(true)}
                className="flex items-center gap-2 px-6 py-2 bg-slate-900 text-white rounded-full text-xs font-bold shadow-lg shadow-slate-900/20 hover:scale-105 transition-transform"
              >
                <Cloud className="w-3 h-3" />
                Sign In to Save Progress
              </button>
            )}
          </div>
        </div>

        {/* Digital Footprint */}
        <Footprint />

        {/* Mini Masjid (Companion Mode) */}
        <MiniMasjid />

        {/* Badges Section */}
        <div>
          <h3 className={`font-bold mb-4 flex items-center gap-2 ${profile.settings.darkMode ? 'text-white' : 'text-slate-900'}`}>
            <Award className="w-5 h-5 text-emerald-500" /> Achievements
          </h3>
          <div className="grid grid-cols-4 gap-3">
            {badges.map(badge => {
              const isUnlocked = badge.unlocked !== undefined ? badge.unlocked : totalDeeds >= badge.threshold;
              return (
                <div key={badge.id} className="flex flex-col items-center gap-2">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-2xl border-2 transition-all ${
                    isUnlocked 
                      ? `${profile.settings.darkMode ? 'bg-emerald-900/30 border-emerald-500/50' : 'bg-emerald-50 border-emerald-200'} opacity-100` 
                      : `${profile.settings.darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-100 border-slate-200'} opacity-40 grayscale`
                  }`}>
                    {badge.icon}
                  </div>
                  <span className={`text-[10px] font-medium text-center ${profile.settings.darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                    {badge.name}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Settings Section */}
        <div className="space-y-4">
           <h3 className={`font-bold mb-2 flex items-center gap-2 ${profile.settings.darkMode ? 'text-white' : 'text-slate-900'}`}>
            <Settings className="w-5 h-5 text-emerald-500" /> Settings
          </h3>
          
          <div className={`p-4 rounded-xl flex items-center justify-between ${profile.settings.darkMode ? 'bg-slate-800' : 'bg-white border border-slate-100'}`}>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${profile.settings.darkMode ? 'bg-slate-700 text-purple-400' : 'bg-purple-50 text-purple-600'}`}>
                {profile.settings.darkMode ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </div>
              <div className="text-sm font-medium">Dark Mode</div>
            </div>
            <button 
              onClick={() => updateSettings({ darkMode: !profile.settings.darkMode })}
              className={`w-12 h-6 rounded-full transition-colors relative ${profile.settings.darkMode ? 'bg-emerald-500' : 'bg-slate-300'}`}
            >
              <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${profile.settings.darkMode ? 'left-7' : 'left-1'}`} />
            </button>
          </div>

          <div className={`p-4 rounded-xl flex items-center justify-between ${profile.settings.darkMode ? 'bg-slate-800' : 'bg-white border border-slate-100'}`}>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${profile.settings.darkMode ? 'bg-slate-700 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
                <Bell className="w-5 h-5" />
              </div>
              <div className="text-sm font-medium">
                Notifications
                {typeof Notification !== 'undefined' && Notification.permission === 'denied' && (
                   <span className="block text-[10px] text-red-400">Permission denied (Check Settings)</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
                {profile.settings.notifications && (
                    <button 
                        onClick={handleTestNotification}
                        className={`p-1.5 rounded-full ${profile.settings.darkMode ? 'bg-slate-700 text-slate-300 hover:text-white' : 'bg-slate-100 text-slate-500 hover:text-slate-900'}`}
                        title="Test Notification"
                    >
                        <Volume2 className="w-4 h-4" />
                    </button>
                )}
                <button 
                onClick={handleNotificationToggle}
                className={`w-12 h-6 rounded-full transition-colors relative ${profile.settings.notifications ? 'bg-emerald-500' : 'bg-slate-300'}`}
                >
                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${profile.settings.notifications ? 'left-7' : 'left-1'}`} />
                </button>
            </div>
          </div>

          <div className={`p-4 rounded-xl flex items-center justify-between ${profile.settings.darkMode ? 'bg-slate-800' : 'bg-white border border-slate-100'}`}>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${profile.settings.darkMode ? 'bg-slate-700 text-orange-400' : 'bg-orange-50 text-orange-600'}`}>
                <Clock className="w-5 h-5" />
              </div>
              <div className="text-sm font-medium">Prayer Alerts</div>
            </div>
            <button 
              onClick={() => updateSettings({ prayerAlerts: !profile.settings.prayerAlerts })}
              className={`w-12 h-6 rounded-full transition-colors relative ${profile.settings.prayerAlerts ? 'bg-emerald-500' : 'bg-slate-300'}`}
            >
              <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${profile.settings.prayerAlerts ? 'left-7' : 'left-1'}`} />
            </button>
          </div>

          <div className={`p-4 rounded-xl flex items-center justify-between border-2 ${profile.settings.kidsMode ? 'bg-amber-50 border-amber-200' : (profile.settings.darkMode ? 'bg-slate-800 border-transparent' : 'bg-white border-slate-100')}`}>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${profile.settings.kidsMode ? 'bg-amber-100 text-amber-600' : (profile.settings.darkMode ? 'bg-slate-700 text-pink-400' : 'bg-pink-50 text-pink-500')}`}>
                {profile.settings.kidsMode ? <Smile className="w-5 h-5" /> : <Baby className="w-5 h-5" />}
              </div>
              <div>
                <div className={`text-sm font-bold ${profile.settings.kidsMode ? 'text-amber-900' : ''}`}>Kids Mode</div>
                {profile.settings.kidsMode && <div className="text-[10px] text-amber-700">Simplified UI enabled</div>}
              </div>
            </div>
            <button 
              onClick={() => updateSettings({ kidsMode: !profile.settings.kidsMode })}
              className={`w-12 h-6 rounded-full transition-colors relative ${profile.settings.kidsMode ? 'bg-amber-500' : 'bg-slate-300'}`}
            >
              <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${profile.settings.kidsMode ? 'left-7' : 'left-1'}`} />
            </button>
          </div>

        </div>

        <div className={`text-center text-xs mt-8 ${profile.settings.darkMode ? 'text-slate-600' : 'text-slate-400'}`}>
          Habl.Life v1.0.1 • Roadmap to Jannah
        </div>
        
        {/* EMERGENCY RESTORE BUTTON */}
        <div className="flex justify-center mt-4">
            <button 
                onClick={() => {
                    if (confirm('Restore lost coins? (Only use if balance is 0)')) {
                        setHablCoins(43); // Manual override using top-level hook
                        localStorage.setItem('habl_coins', '43');
                        alert('Coins restored to 43. Please refresh to sync.');
                        window.location.reload();
                    }
                }}
                className="text-[10px] text-slate-500 underline opacity-50 hover:opacity-100"
            >
                Restore Legacy Coins
            </button>
        </div>

      </div>
      
      <AuthModal 
        isOpen={isAuthOpen} 
        onClose={() => setIsAuthOpen(false)} 
        darkMode={profile.settings.darkMode} 
      />
    </div>
  );
}
